import Controller from "@ember/controller";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import ConfirmSession from "discourse/components/dialog-messages/confirm-session";
import AuthTokenModal from "discourse/components/modal/auth-token";
import ForgotPassword from "discourse/components/modal/forgot-password";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import CanCheckEmailsHelper from "discourse/lib/can-check-emails-helper";
import logout from "discourse/lib/logout";
import { userPath } from "discourse/lib/url";
import { isWebauthnSupported } from "discourse/lib/webauthn";
import { i18n } from "discourse-i18n";

// Number of tokens shown by default.
const DEFAULT_AUTH_TOKENS_COUNT = 2;

export default class SecurityController extends Controller {
  @service modal;
  @service dialog;
  @service router;
  @service currentUser;

  passwordProgress = null;
  subpageTitle = i18n("user.preferences_nav.security");
  showAllAuthTokens = false;

  @computed("siteSettings.moderators_view_emails")
  get canModeratorsViewEmails() {
    return this.siteSettings.moderators_view_emails;
  }

  @computed("model.user_auth_tokens.length")
  get canShowAllAuthTokens() {
    return this.model?.user_auth_tokens?.length > DEFAULT_AUTH_TOKENS_COUNT;
  }

  @computed("model.id", "currentUser.id")
  get canCheckEmails() {
    return new CanCheckEmailsHelper(
      this.model.id,
      this.canModeratorsViewEmails,
      this.currentUser
    ).canCheckEmails;
  }

  @computed("model.staged")
  get canResetPassword() {
    return !this.model.staged;
  }

  get isCurrentUser() {
    return this.currentUser?.id === this.model.id;
  }

  get canUsePasskeys() {
    return (
      !this.siteSettings.enable_discourse_connect &&
      this.siteSettings.enable_local_logins &&
      this.siteSettings.enable_passkeys &&
      isWebauthnSupported()
    );
  }

  @computed("model.is_anonymous")
  get canChangePassword() {
    if (this.model?.is_anonymous) {
      return false;
    } else {
      return (
        !this.siteSettings.enable_discourse_connect &&
        this.siteSettings.enable_local_logins
      );
    }
  }

  @computed("showAllAuthTokens", "model.user_auth_tokens")
  get authTokens() {
    this.model?.user_auth_tokens?.sort((a, b) => {
      if (a.is_active) {
        return -1;
      } else if (b.is_active) {
        return 1;
      } else {
        return b.seen_at.localeCompare(a.seen_at);
      }
    });

    return this.showAllAuthTokens
      ? this.model?.user_auth_tokens
      : this.model?.user_auth_tokens?.slice(0, DEFAULT_AUTH_TOKENS_COUNT);
  }

  @computed(
    "model.is_anonymous",
    "model.no_password",
    "siteSettings",
    "model.user_passkeys",
    "model.associated_accounts",
    "model.can_remove_password"
  )
  get canRemovePassword() {
    // Hint returned from staff-info controller that
    // works even if staff hasn't revealed e-mails.
    if (this.model?.can_remove_password) {
      return true;
    }

    if (
      this.model?.is_anonymous ||
      this.model?.no_password ||
      this.siteSettings.enable_discourse_connect ||
      !this.siteSettings.enable_local_logins
    ) {
      return false;
    }

    return (
      this.model?.associated_accounts?.length > 0 ||
      (this.canUsePasskeys && this.model?.user_passkeys?.length > 0)
    );
  }

  get associatedAccountsLoaded() {
    return typeof this.model.associated_accounts !== "undefined";
  }

  @action
  changePassword(event) {
    event?.preventDefault();
    if (!this.passwordProgress) {
      this.set("passwordProgress", i18n("user.change_password.in_progress"));
      return this.model
        .changePassword()
        .then((result) => {
          if (result.email_code) {
            this.set("passwordProgress", null);
            this.modal.show(ForgotPassword, {
              model: {
                codeSent: true,
                emailOrUsername: this.model.email || this.model.username,
              },
            });
            return;
          }

          this.setProperties({
            changePasswordProgress: false,
            passwordProgress: i18n("user.change_password.success"),
          });
        })
        .catch(() => {
          // password failed to change
          this.setProperties({
            changePasswordProgress: false,
            passwordProgress: i18n("user.change_password.error"),
          });
        });
    }
  }

  removePasswordConfirm() {
    this.dialog.deleteConfirm({
      title: i18n("user.change_password.remove"),
      message: i18n("user.change_password.remove_detail"),
      confirmButtonLabel: "user.change_password.remove",
      confirmButtonIcon: "trash-can",
      didConfirm: () => {
        this.set("removePasswordInProgress", true);

        this.model
          .removePassword()
          .then((response) => {
            this.set("removePasswordInProgress", false);
            if (response.success) {
              this.model.set("no_password", true);
            }
          })
          .catch((error) => {
            this.set("removePasswordInProgress", false);
            popupAjaxError(error);
          });
      },
    });
  }

  @action
  async removePassword(event) {
    event?.preventDefault();
    if (this.removePasswordInProgress) {
      return;
    }

    try {
      const trustedSession = await this.model.trustedSession();

      if (!trustedSession.success) {
        this.dialog.dialog({
          title: i18n("user.confirm_access.title"),
          type: "notice",
          bodyComponent: ConfirmSession,
          didConfirm: () => this.removePasswordConfirm(),
        });
      } else {
        this.removePasswordConfirm();
      }
    } catch (error) {
      popupAjaxError(error);
    }
  }

  @action
  toggleShowAllAuthTokens(event) {
    event?.preventDefault();
    this.toggleProperty("showAllAuthTokens");
  }

  @action
  revokeAuthToken(token, event) {
    event?.preventDefault();
    ajax(
      userPath(
        `${this.get("model.username_lower")}/preferences/revoke-auth-token`
      ),
      {
        type: "POST",
        data: token ? { token_id: token.id } : {},
      }
    )
      .then(() => {
        if (!token) {
          logout();
        } // All sessions revoked
      })
      .catch(popupAjaxError);
  }

  @action
  async manage2FA() {
    try {
      const trustedSession = await this.model.trustedSession();

      if (!trustedSession.success) {
        this.dialog.dialog({
          title: i18n("user.confirm_access.title"),
          type: "notice",
          bodyComponent: ConfirmSession,
          didConfirm: () =>
            this.router.transitionTo("preferences.second-factor"),
        });
      } else {
        await this.router.transitionTo("preferences.second-factor");
      }
    } catch (error) {
      popupAjaxError(error);
    }
  }

  @action
  save() {
    this.set("saved", false);

    return this.model.then(() => this.set("saved", true)).catch(popupAjaxError);
  }

  @action
  showToken(token) {
    this.modal.show(AuthTokenModal, { model: token });
  }
}
